<template>
  <GuideTour v-if="showTour" />
  <QuickFeedback></QuickFeedback>
  <TabView ref="tabComponentRef" />
</template>

<script setup lang="ts">
import GuideTour from '@/components/GuideTour.vue'
import QuickFeedback from '@/components/QuickFeedback.vue'
import TabView from '@/components/TabView.vue'
import { sendMessage, log, logError } from '@/utils/index'
import { onMounted, onUnmounted, ref } from 'vue'
import { useAuthStore } from '../stores/auth'
import * as Sentry from '@sentry/vue'
import { latestExtensionVersion } from '@/globals/env'
import { syncSession } from '@/services/auth'
import UserActivityException from '@/exceptions/UserActivityException'
import NotReportableException from '@/exceptions/NotReportableException'
import RequestException from '@/exceptions/RequestException'
import { useTaskStatusStore } from '@/stores/taskStatus'

const authStore = useAuthStore()
const tabComponentRef = ref()
const showTour = ref(false);
const taskStatus = useTaskStatusStore()

onMounted(() => {
  //check current user extension version, site they are on, etc. and notify if outdated or action is nrequired
  sendMessage({ message: 'get-session-details' })

  //reset listeners to prevent duplicate listeners
  window.addEventListener('message', messagesHandler)

  //regular session check
  setInterval(async () => {
    await syncSession();
  }, 60 * 60 * 1000)
})

onUnmounted(() => {
  //reset listeners to prevent duplicate listeners
  window.removeEventListener('message', messagesHandler)
})

//This will handle messages sent from the extension to the web server
const messagesHandler = async (event: any) => {
  //TODO:We are sending version in the iframe url, no need to request from extension
  if (event.data.message == 'get-session-details-response') {
    const userVersion = event.data.currentVersion

    //show a warning if the extension is outdated
    if (userVersion != latestExtensionVersion) {
      log('outdated version: ' + userVersion)
      const content =
        'You are using an outdated version of PoshPop. Please update to the latest version on the web store.'
      tabComponentRef.value.displayWarnMessage({ content, critical: true })
    }

    //show initial tour
    if (event.data.isFirstTimeRunning)
      showTour.value = true

    //log("extension up to date: " + userVersion)
  } else if (event.data.message == 'refresh-token') {
    try {
      log('syncing user session ')
      await authStore.refreshToken()
      sendMessage({ message: 'refresh-token-complete' })
    } catch (err) {
      sendMessage({ message: 'refresh-token-error', error: err })

      if (!(
        err instanceof UserActivityException ||
        err instanceof NotReportableException ||
        err instanceof RequestException
      )) {
        Sentry.captureException(err)
      }

      logError('error syncing user session: ', err)
    }
  } else if (event.data.message == "track-share-progress") {
    //update reactive data 
    taskStatus.changeShareTaskProgress(event.data)
  } else if (event.data.message == "track-relist-progress") {
    //update reactive data 
    taskStatus.changeRelistTaskProgress(event.data)
  } else if (event.data.message == "track-follow-progress") {
    //update reactive data 
    taskStatus.changeFollowTaskProgress(event.data)

  } else if (event.data.message == "track-follow-by-username-progress") {
    //update reactive data 
    taskStatus.changeFollowByUsernameTaskProgress(event.data)

  } else if (event.data.message == "track-follow-newly-joined-closets-progress") {
    //update reactive data 
    taskStatus.changeFollowJustJoinedTaskProgress(event.data)

  } else if (event.data.message == "track-unfollow-progress") {
    //update reactive data 
    taskStatus.changeUnfollowTaskProgress(event.data)

  } else if (event.data.message == "track-bulk-offer-progress") {
    //update reactive data 
    taskStatus.changeBulkOfferTaskProgress(event.data)

  } else if (event.data.message == "track-bulk-price-progress") {
    //update reactive data 
    taskStatus.changeBulkPriceTaskProgress(event.data)

  } else if (event.data.message == "track-community-share-progress") {
    //update reactive data 
    taskStatus.changeCommunityShareTaskProgress(event.data)

  }

}
</script>
